/**
 * Payment Success Page
 * 
 * Displayed after successful payment.
 * Shows different messages for guest vs authenticated users.
 */
import React from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { 
  CheckCircle, 
  Mail, 
  ArrowRight, 
  Download,
  LayoutDashboard,
  LogIn
} from 'lucide-react';

const PaymentSuccess = () => {
  const location = useLocation();
  const state = location.state;
  
  // Redirect to home if no state (direct access)
  if (!state) {
    return <Navigate to="/pricing" replace />;
  }
  
  const { subscription, isGuest, planName, planCategory, isFreeSubscription, account } = state;
  
  const isStockRecommendations = planCategory === 'stock_recommendations';

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-6">
      <div className="max-w-lg w-full">
        {/* Success Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-green-600" size={48} />
          </div>
          
          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isFreeSubscription ? 'Subscription Activated!' : 'Payment Successful!'}
          </h1>
          
          {/* Subtitle based on user type */}
          <p className="text-gray-600 mb-6">
            {isGuest 
              ? 'Your account has been created and subscription is now active.'
              : 'Your subscription has been activated successfully.'
            }
          </p>
          
          {/* Subscription Details */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold text-gray-900 mb-3">Subscription Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Plan:</span>
                <span className="font-medium">{planName || subscription?.plan_name}</span>
              </div>
              {subscription?.plan_duration && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Duration:</span>
                  <span className="font-medium">{subscription.plan_duration} Months</span>
                </div>
              )}
              {subscription?.amount && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount Paid:</span>
                  <span className="font-medium text-green-600">
                    ₹{Number(subscription.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              {subscription?.expiry_date && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Valid Until:</span>
                  <span className="font-medium">
                    {new Date(subscription.expiry_date).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              )}
              {subscription?.transaction_id && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Transaction ID:</span>
                  <span className="font-mono text-xs">{subscription.transaction_id}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Guest User Message - show for both paid and free subscriptions */}
          {isGuest && (subscription?.account || account) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-start gap-3">
                <Mail className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-1">
                    Check Your Email
                  </h4>
                  <p className="text-sm text-blue-800 mb-2">
                    We've sent your login credentials to <strong>{(account || subscription?.account)?.email}</strong>.
                    Please check your inbox (and spam folder) for the welcome email.
                  </p>
                  <div className="bg-white rounded p-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Email:</span>
                      <span className="font-mono">{(account || subscription?.account)?.email}</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-gray-600">Password:</span>
                      <span className="font-mono">{(account || subscription?.account)?.password}</span>
                    </div>
                  </div>
                  <p className="text-xs text-red-600 mt-2 font-medium">
                    ⚠️ Please save these credentials and change your password after login.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Performance Reports Message (Stock Recommendations only) */}
          {isStockRecommendations && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-start gap-3">
                <Download className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <h4 className="font-semibold text-yellow-900 mb-1">
                    Performance Reports
                  </h4>
                  <p className="text-sm text-yellow-800">
                    You'll receive our performance reports via email shortly. 
                    You can also access them from your dashboard.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Invoice Message - only for paid subscriptions */}
          {!isFreeSubscription && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-start gap-3">
                <Mail className="text-gray-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">
                    Invoice
                  </h4>
                  <p className="text-sm text-gray-600">
                    A confirmation email with your invoice has been sent to your email address.
                    You can also download invoices from your dashboard.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="space-y-3">
            {isGuest ? (
              <Link
                to="/login"
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors"
              >
                <LogIn size={20} />
                Go to Login
              </Link>
            ) : (
              <Link
                to="/dashboard"
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors"
              >
                <LayoutDashboard size={20} />
                Go to Dashboard
              </Link>
            )}
            
            <Link
              to="/"
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-full font-semibold hover:bg-gray-200 transition-colors"
            >
              Back to Home
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
        
        {/* Support Info */}
        <p className="text-center text-gray-500 text-sm mt-6">
          Need help? Contact us at{' '}
          <a href="mailto:support@climbcapital.in" className="text-blue-600 hover:underline">
            support@climbcapital.in
          </a>
        </p>
      </div>
    </div>
  );
};

export default PaymentSuccess;
